import React, { useState, useEffect, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
    getClassProduct,
    getClassProductsBySport,
    getSportsTypes,
} from "api/class";
import ProductCard from "components/card/ProductCard";
import { sportCategories } from "assets/data/mainItems";
import NoData from "components/common/NoData";
import Blank from "components/common/Blank";
import LoadingSpinner from "components/common/LoadingSpiner";
import SubHeader from "components/common/SubHeader";
import Tab from "../../../components/Tab";
import cartIcon from "assets/images/icons/cart_icon.svg";
import homeIcon from "assets/images/icons/ic_home_line.svg";
import useRefreshCallback from "hooks/useRefreshCallback";
import useUserStore from "@store/useUserStore";
import Seo from "components/common/Seo";

// 소분류: 특강은 allowed_ticket_types === "GENERAL_1T10"만 해당하고 그 외는 모두 수강권.
// 모든 스포츠에 기본 "전체"가 있고, 프리다이빙만 수강권/특강으로 세분화한다.
const SPECIAL_LECTURE_TICKET_TYPE = "GENERAL_1T10";

const getSubCategories = (sportName) =>
    sportName === "프리다이빙" ? ["전체", "수강권", "특강"] : ["전체"];

// 소분류 ↔ URL(?tab=) 토큰 매핑. "전체"는 기본값이므로 URL에서 생략한다.
// 배너 등 외부 진입: /store/class?sport=<프리다이빙id>&tab=special
const SUB_CATEGORY_TO_TAB = { 수강권: "ticket", 특강: "special" };
const TAB_TO_SUB_CATEGORY = { ticket: "수강권", special: "특강" };

function ClassPage() {
    const navigate = useNavigate();
    const [, setSearchParams] = useSearchParams();
    const userInfo = useUserStore((state) => state.userInfo);

    const [sportsList, setSportsList] = useState([]);
    // 선택 스포츠는 id로 관리 (URL ?sport=1 의 값이 곧 스포츠 id)
    const [selectedSportId, setSelectedSportId] = useState(() => {
        const sport = new URLSearchParams(window.location.search).get("sport");
        return sport != null && sport !== "" ? Number(sport) : null;
    });
    // 소분류도 URL ?tab= 값으로 초기화 (배너 등에서 특강 선택 상태로 바로 진입)
    const [selectedSubCategory, setSelectedSubCategory] = useState(() => {
        const tab = new URLSearchParams(window.location.search).get("tab");
        return TAB_TO_SUB_CATEGORY[tab] || "전체";
    });
    const [originalProducts, setOriginalProducts] = useState([]);
    const [displayProducts, setDisplayProducts] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [isSportsLoading, setIsSportsLoading] = useState(true); // 스포츠 목록 로딩 상태 추가
    const [hasError, setHasError] = useState(false);

    // 스포츠 id별 상품 목록 캐시 (탭 전환 시 재요청 방지)
    // key: selectedSportId(null 포함), value: 상품 배열
    const productCacheRef = useRef(new Map());

    // 선택 스포츠 이름 (필터·소분류 판별용) — id로부터 파생
    const selectedSportName = sportsList.find(
        (s) => s.id === selectedSportId
    )?.name;

    useRefreshCallback(() => {
        // 새로고침 시 캐시를 비워 이후 탭 전환에서 최신 데이터를 다시 받도록 한다.
        productCacheRef.current.clear();
        return getClassProduct().then((res) => {
            if (res && Array.isArray(res)) {
                setOriginalProducts(res);
            }
        });
    });

    // 스포츠 타입 목록 가져오기
    useEffect(() => {
        const fetchSportsTypes = async () => {
            try {
                setIsSportsLoading(true);
                const res = await getSportsTypes();

                // API 응답 검증
                if (res && res.sports && Array.isArray(res.sports)) {
                    const sortedSports = res.sports.sort((a, b) => a.id - b.id);
                    const combinedSports = sortedSports.map((sport) => {
                        const targetSport = sportCategories.find(
                            (category) => category.title === sport.name
                        );
                        return {
                            ...sport,
                            // img: targetSport?.img || "",
                            description: targetSport?.description || "",
                            src: targetSport?.src || "",
                        };
                    });
                    setSportsList(combinedSports);
                } else {
                    console.warn(
                        "스포츠 타입 API 응답이 예상과 다릅니다:",
                        res
                    );
                    setSportsList([]);
                }
            } catch (error) {
                console.error("스포츠 타입 로딩 에러:", error);
                setSportsList([]);
            } finally {
                setIsSportsLoading(false);
            }
        };

        fetchSportsTypes();
    }, []);

    // 스포츠 목록 로드 후 선택 스포츠 확정 (URL id가 없거나 유효하지 않으면 선호/프리다이빙/첫번째)
    useEffect(() => {
        if (sportsList.length === 0) return;
        if (sportsList.some((s) => s.id === selectedSportId)) return;

        const initialSport =
            sportsList.find((s) => s.name === userInfo?.preferred_sport_name) ||
            sportsList.find((s) => s.name === "프리다이빙") ||
            sportsList[0];
        if (initialSport) setSelectedSportId(initialSport.id);
    }, [sportsList, selectedSportId, userInfo?.preferred_sport_name]);

    // 선택 스포츠를 URL(?sport=id)에 동기화
    // setSearchParams는 URL 변경 시 identity가 바뀌므로 deps에서 제외한다(무한 루프 방지).
    useEffect(() => {
        if (selectedSportId == null) return;
        setSearchParams(
            (prev) => {
                const next = new URLSearchParams(prev);
                next.set("sport", String(selectedSportId));
                return next;
            },
            { replace: true }
        );
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedSportId]);

    // 선택 소분류를 URL(?tab=)에 동기화. "전체"는 기본값이라 파라미터를 제거해 URL을 깔끔히 유지.
    useEffect(() => {
        setSearchParams(
            (prev) => {
                const next = new URLSearchParams(prev);
                const token = SUB_CATEGORY_TO_TAB[selectedSubCategory];
                if (token) next.set("tab", token);
                else next.delete("tab");
                return next;
            },
            { replace: true }
        );
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedSubCategory]);

    useEffect(() => {
        const fetchProducts = async () => {
            // 캐시 히트: 재요청 없이 즉시 사용
            if (productCacheRef.current.has(selectedSportId)) {
                const cached = productCacheRef.current.get(selectedSportId);
                setOriginalProducts(cached);
                setDisplayProducts(cached);
                setHasError(false);
                setIsLoading(false);
                return;
            }
            try {
                setIsLoading(true);
                setHasError(false);
                // 스포츠 미선택(null)이면 전체 목록, 선택 시 스포츠별 목록
                const res =
                    selectedSportId == null
                        ? await getClassProduct()
                        : await getClassProductsBySport(selectedSportId);
                if (res && Array.isArray(res)) {
                    productCacheRef.current.set(selectedSportId, res);
                    setOriginalProducts(res);
                    setDisplayProducts(res);
                } else {
                    console.warn("API 응답이 배열이 아닙니다:", res);
                    setOriginalProducts([]);
                    setDisplayProducts([]);
                }
            } catch (error) {
                console.error("클래스 상품 로딩 에러:", error);
                setHasError(true);
                setOriginalProducts([]);
                setDisplayProducts([]);
            } finally {
                setIsLoading(false);
            }
        };

        fetchProducts();
    }, [selectedSportId]);

    useEffect(() => {
        if (!selectedSportName) return;

        let filteredProducts;
        if (selectedSportName === "원모어") {
            filteredProducts = originalProducts.filter(
                (product) => product.brand === selectedSportName
            );
        } else if (selectedSportName === "특강") {
            filteredProducts = originalProducts.filter(
                (product) =>
                    product.brand === selectedSportName ||
                    product.name.includes(selectedSportName)
            );
        } else {
            filteredProducts = originalProducts.filter(
                (product) => product.sport_name === selectedSportName
            );
        }

        // 프리다이빙 소분류(수강권/특강) 필터
        if (selectedSportName === "프리다이빙" && selectedSubCategory !== "전체") {
            filteredProducts = filteredProducts.filter((product) =>
                selectedSubCategory === "특강"
                    ? product.allowed_ticket_types === SPECIAL_LECTURE_TICKET_TYPE
                    : product.allowed_ticket_types !== SPECIAL_LECTURE_TICKET_TYPE
            );
        }

        setDisplayProducts(filteredProducts);
    }, [selectedSportName, selectedSubCategory, originalProducts]);

    // 스포츠 목록과 상품 모두 로딩 중일 때만 로딩 스피너 표시
    if (isLoading || isSportsLoading) {
        return <LoadingSpinner />;
    }

    return (
        <div
            className={`class-list-page${selectedSportName ? " class-list-page--has-sub" : ""}`}
            role="main"
            aria-label="클래스 페이지"
        >
            <Seo
                title="다이빙 강습"
                description="프리다이빙·스쿠버다이빙 강습(클래스)을 조회하고 예약하세요. 더딥스, 와이낫다이브 등 전문 다이빙 업체의 수강권을 한 곳에서 비교할 수 있습니다."
            />
            <SubHeader
                title="수강권"
                menus={[
                  {
                    name: "홈",
                    icon: homeIcon,
                    callback: () => {
                        if (window.location.pathname !== "/") navigate("/");
                    },
                },
                    {
                        name: "장바구니",
                        icon: cartIcon,
                        callback: () => navigate("/cart"),
                    },
                ]}
            />
            {/* sportsList가 로드된 후에만 Tab 렌더링 */}
            {sportsList.length > 0 && (
                <Tab
                    tabs={sportsList.map((category) => ({
                        id: category.id,
                        name: category.name,
                    }))}
                    defaultActiveTab={
                        sportsList.findIndex((s) => s.id === selectedSportId) + 1
                    }
                    onTabChange={(id) => {
                        setSelectedSportId(id);
                        setSelectedSubCategory("전체");
                    }}
                    marginTop={48}
                />
            )}
            {/* ── 소분류 (전체 / 수강권 / 특강) — 모든 스포츠에 기본 "전체" 노출 ── */}
            {selectedSportName && (
                <div
                    className="class-list-page__sub-category-chips"
                    role="listbox"
                    aria-label="소분류"
                >
                    {getSubCategories(selectedSportName).map((sub) => (
                        <button
                            key={sub}
                            type="button"
                            role="option"
                            aria-selected={selectedSubCategory === sub}
                            className={`sub-category-chip${selectedSubCategory === sub ? " active" : ""}`}
                            onClick={() => setSelectedSubCategory(sub)}
                        >
                            {sub}
                        </button>
                    ))}
                </div>
            )}
            <div className="product-grid-wrap" role="region">
                {displayProducts.length > 0 ? (
                    displayProducts.map((product) => (
                        <ProductCard
                            key={
                                product.created_at ||
                                product.id ||
                                Math.random()
                            }
                            info={product}
                            link={`/class/detail/${product.id}`}
                        />
                    ))
                ) : (
                    <NoData
                        style={{ gridColumn: "1 / -1" }}
                        title="해당 카테고리의 클래스가 없습니다."
                    />
                )}
            </div>
            <Blank height={40} />
        </div>
    );
}

export default ClassPage;
